import React from 'react';
import { FiBell, FiSettings, FiUser, FiLogOut, FiMenu } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useIsMobile } from '../hooks/use-is-mobile';
import { useSidebar } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

function UserAvatar({ name, src }) {
  const initials = (name || 'U')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  if (src) {
    return <img src={src} alt="" className="h-9 w-9 rounded-full object-cover" />;
  }
  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-500 text-xs font-semibold text-white">
      {initials}
    </div>
  );
}

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { setOpenMobile } = useSidebar();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-[999] flex h-[60px] items-center border-b border-slate-100 bg-white px-4 shadow-sm lg:h-[70px] lg:px-6'
      )}
    >
      <div className="flex h-full w-full items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          {isMobile && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              aria-label="Open menu"
              onClick={() => setOpenMobile(true)}
            >
              <FiMenu className="h-5 w-5" />
            </Button>
          )}
          <span className="hidden text-base font-semibold text-slate-700 sm:block sm:text-lg">Admin Dashboard</span>
          <span className="text-base font-semibold text-slate-700 sm:hidden">Dashboard</span>
        </div>

        <div className="flex items-center gap-2 lg:gap-4">
          <Button type="button" variant="ghost" size="icon" className="hidden md:flex" aria-label="Notifications">
            <FiBell className="h-5 w-5" />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="hidden md:flex" aria-label="Settings">
            <FiSettings className="h-5 w-5" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded-full outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-brand-500"
                aria-label="User menu"
              >
                <UserAvatar name={user?.name} src={user?.pict} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-slate-700">{user?.name || 'User'}</span>
                  <span className="text-xs font-normal text-slate-500">{user?.email || ''}</span>
                  {user?.position_name && <span className="mt-1 text-xs text-slate-500">{user.position_name}</span>}
                  {user?.organization_name && <span className="text-xs text-slate-500">{user.organization_name}</span>}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <FiUser className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem>
                <FiSettings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={handleLogout}>
                <FiLogOut className="mr-2 h-4 w-4" />
                Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
